/**
 * Module dependencies.
 */

import {
  ArrowNavigation,
  Image,
  Loading,
  Slide,
  Slider,
  Subtitle,
  Type
} from 'src/components/core';

import { GetStaticProps, NextPage } from 'next/types';
import { History as HistoryProps } from 'src/types/history';
import { LayoutProps } from 'src/components/layout';
import {
  RawHtml,
  color,
  media,
  normalizeNewLine,
  units
} from '@untile/react-components';

import { decadesBackgroundColors } from 'src/core/content-config/our-story';
import { fetchHistory } from 'src/api/history/fetch-history';
import { ifProp, prop, theme } from 'styled-tools';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'src/components/core/layout/container';
import Metatags from 'src/components/core/metatags';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import size from 'lodash/size';
import styled, { css } from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import useHistory from 'src/api/history/use-history';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  initialData: HistoryProps[];
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  min-height: 100vh;
  overflow: hidden;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + 34px) 0
    ${units(12)};
  position: relative;
  transition: background-color 1s ease;
  width: 100vw;

  ${media.min('md')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(
    10
  )}) 0 ${units(5)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  display: grid;
  grid-template-areas:
    '.       topWrapper topWrapper'
    'decade  decade     decade'
    'gallery gallery    gallery'
    '.       scrollbar  .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  padding: 0;

  ${media.min('md')`
    grid-template-areas:
      '.         topWrapper    topWrapper    topWrapper    .         .'
      '.         .             decade        decade        decade    .'
      'gallery   gallery       gallery       gallery       gallery   gallery'
      '.         navigation    navigation    navigation    .         .'
      '.         scrollbar     scrollbar     scrollbar     .         .';
    grid-template-columns: 1.2fr 5fr 2fr 3fr 0.4fr 0.6fr;
  `}

  ${media.min('lg')`
    grid-template-columns: 1fr 5fr 2fr 3fr 0.4fr 0.6fr;
  `}
`;

/**
 * `TopWrapper` styled component.
 */

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: topWrapper;
  justify-content: space-between;
  margin-bottom: ${units(4)};
  overflow: hidden;
  z-index: 1;

  ${media.min('xs')`
    margin-bottom: ${units(8)};
  `}

  ${media.min('md')`
    align-items: center;
    flex-direction: row;
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  margin-bottom: ${units(1)};

  ${media.min('xs')`
    margin-bottom: ${units(3)};
  `}

  ${media.min('md')`
    margin-bottom: 0;
  `}
`;

/**
 * `DecadesList` styled component.
 */

const DecadesList = styled.div`
  align-items: center;
  display: grid;
  grid-column-gap: ${units(2)};
  grid-template-columns: repeat(7, max-content);
  justify-self: flex-end;
  overflow-x: auto;
`;

/**
 * `DecadeButton` styled component.
 */

const DecadeButton = styled.button<{ isActive?: boolean }>`
  ${theme('typography.styles.p')}

  background: none;
  border: none;
  cursor: pointer;
  outline: 0;
  padding: 0;
  transition: ${theme('animations.defaultTransition')};
  transition-property: border, opacity;

  &:hover {
    opacity: 0.8;
  }

  &:last-of-type {
    ${media.max('md')`
      margin-right: ${theme('grid.gutterMobile')}px;
    `}
  }

  ${ifProp(
    'isActive',
    css`
      border-bottom: 1px solid ${color('textColor')};
    `
  )}
`;

/**
 * `DecadeBackground` styled component.
 */

const DecadeBackground = styled(Type.H1)`
  align-self: start;
  color: ${color.transparentize('white', 0.2)};
  font-size: 54vw;
  grid-area: decade;
  grid-row: 2 / 4;
  line-height: 54vw;
  margin-left: -10vw;
  margin-top: -5vw;
  position: relative;

  ${media.min('sm')`
    font-size: 36vw;
    line-height: 36vw;
    margin-left: 0vw;
    margin-top: -3vw;
  `}

  ${media.min('md')`
    font-size: 20vw;
    grid-row: 1 / 3;
    line-height: 20vw;
    margin-left: -1vw;
    margin-top: -2vw;
  `}
`;

/**
 * `HistoryWrapper` styled component.
 */

const HistoryWrapper = styled.div`
  display: grid;
  grid-template-areas:
    '. title'
    '. description'
    '. image';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr;
  grid-template-rows: max-content 1fr max-content;
  height: 100%;
  width: 100%;

  ${media.min('md')`
    grid-template-areas:
      '. image . .'
      '. image . title'
      '. image . description'
      '. image . .';
    grid-template-columns: 1fr 4.8fr 0.4fr 3fr;
    grid-template-rows: ${units(5)} repeat(3, max-content);
  `}

  ${media.min('lg')`
    grid-template-columns: 1.1fr 5.3fr 0.4fr 3fr;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  margin-bottom: ${units(3)};
  overflow: hidden;
  padding-bottom: 67.8%;
  position: relative;

  ${media.min('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `HistoryTitle` styled component.
 */

const HistoryTitle = styled(Type.H2)`
  grid-area: title;
  margin-bottom: ${units(1)};
`;

/**
 * `HistoryDescription` styled component.
 */

const HistoryDescription = styled(Subtitle).attrs({ size: 'medium' })`
  grid-area: description;
  margin-bottom: ${units(5)};

  br {
    content: '';
    display: block;
    margin-bottom: ${units(1)};
  }

  ${media.min('xs')`
    margin-bottom: ${units(9)};
  `}

  ${media.min('md')`
    margin-bottom: 0;
  `}
`;

/**
 * `GalleryWrapper` styled component.
 */

const GalleryWrapper = styled.div`
  grid-area: gallery;
  grid-row: 2 / 4;
`;

/**
 * `GalleryNavigationWrapper` styled component.
 */

const GalleryNavigationWrapper = styled.div`
  grid-area: navigation;

  ${media.max('md')`
    display: none;
  `}
`;

/**
 * `StyledArrowNavigation` styled component.
 */

const StyledArrowNavigation = styled(ArrowNavigation)`
  margin: 0 auto;
`;

/**
 * `ScrollbarWrapper` styled component.
 */

const ScrollbarWrapper = styled.div`
  grid-area: scrollbar;
  overflow: hidden;
  position: relative;
`;

/**
 * `Scrollbar` styled component.
 */

const Scrollbar = styled.div<{ totalItems: number }>`
  padding: ${units(1)} 0;
  width: 100%;

  .gallery-scrollbar {
    background-color: ${color.transparentize('white', 0.4)};
    height: 1px;
    width: calc(100% + 100% / ${prop('totalItems')});
  }

  .gallery-scrollbar-drag {
    background: ${color('grey800')};
    height: 1px;
    width: calc(100% / ${prop('totalItems')} - 6.38px) !important;

    ${media.min('md')`
      width: calc(100% / ${prop('totalItems')} - 18.75px) !important;
    `}
  }
`;

/**
 * Get carousel config.
 */

function getCarouselConfig() {
  return {
    breakpoints: {
      1200: {
        slidesPerView: 1.32
      }
    },
    slidesPerView: 1.1
  };
}

/**
 * `OurStory` page.
 */

const OurStory: NextPage<Props> = ({ initialData }) => {
  const { translate } = useTranslate();
  const { data, isLoading, isSuccess } = useHistory({ initialData });

  const { decades, histories } = useMemo(() => {
    const histories = orderBy(data, 'year');
    const decades = map(uniqBy(data, 'decade'), ({ decade }) => decade);

    return {
      decades,
      histories
    };
  }, [data]);

  const total = size(histories);
  const sliderRef = useRef<any>();
  const [currentDecade, setCurrentDecade] = useState<string>(
    histories[0].decade
  );

  const [activeSlide, setActiveSlide] = useState<number>(0);
  const handleGoToNext = useCallback(() => {
    setActiveSlide(current => current + 1);

    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideNext();
    }
  }, []);

  const handleGoToPrevious = useCallback(() => {
    setActiveSlide(current => current - 1);

    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slidePrev();
    }
  }, []);

  const handleGoToSlide = useCallback(index => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideTo(index);
    }
  }, []);

  const handleSlideChange = useCallback(
    ({ activeIndex }) => {
      if (activeIndex < total) {
        setActiveSlide(activeIndex);
        setCurrentDecade(histories[activeIndex].decade);

        return;
      }

      handleGoToSlide(0);
      setActiveSlide(0);
      setCurrentDecade(histories[0].decade);
    },
    [handleGoToSlide, histories, total]
  );

  const handleClickDecade = useCallback(
    (decade: string) => {
      const indexOfFirstDecadeHistory = findIndex(histories, [
        'decade',
        decade
      ]);

      setCurrentDecade(decade);
      setActiveSlide(indexOfFirstDecadeHistory);
      handleGoToSlide(indexOfFirstDecadeHistory);
    },
    [handleGoToSlide, histories]
  );

  useEffect(() => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.autoplay.start();

      return;
    }
  }, []);

  return (
    <>
      <Metatags
        description={translate('common:actions.discoverOurTimeline')}
        pageTitle={translate('menu:ourStory')}
        title={translate('menu:ourStory')}
      />

      <Wrapper bgColor={decadesBackgroundColors[currentDecade]}>
        <Loading
          active={isLoading}
          size={48}
        />

        {isSuccess && (
          <StyledContainer>
            <TopWrapper>
              <Title>{translate('menu:ourStory')}</Title>

              <DecadesList>
                {map(decades, (decade, index) => (
                  <DecadeButton
                    isActive={decade === currentDecade}
                    key={index}
                    onClick={() => handleClickDecade(decade)}
                  >
                    {decade}
                  </DecadeButton>
                ))}
              </DecadesList>
            </TopWrapper>

            <DecadeBackground>{currentDecade}</DecadeBackground>

            <GalleryWrapper>
              <Slider
                autoplay={{
                  delay: 10000,
                  disableOnInteraction: false
                }}
                onSlideChange={handleSlideChange}
                parallax
                ref={sliderRef}
                scrollbar={{
                  dragClass: 'gallery-scrollbar-drag',
                  el: '.gallery-scrollbar',
                  hide: false
                }}
                speed={750}
                {...getCarouselConfig()}
              >
                <>
                  {map(histories, ({ description, image, year }, index) => (
                    <Slide key={index}>
                      <HistoryWrapper>
                        <ImageWrapper>
                          <Image
                            alt={`${year}: ${description}`}
                            fill
                            src={image}
                            style={{ objectFit: 'cover' }}
                          />
                        </ImageWrapper>

                        <HistoryTitle data-swiper-parallax={-400}>
                          {year}
                        </HistoryTitle>

                        <HistoryDescription data-swiper-parallax={-550}>
                          <RawHtml>{normalizeNewLine(description)}</RawHtml>
                        </HistoryDescription>
                      </HistoryWrapper>
                    </Slide>
                  ))}

                  <Slide key={total} />
                </>
              </Slider>
            </GalleryWrapper>

            <GalleryNavigationWrapper>
              <StyledArrowNavigation
                activeSlide={activeSlide}
                onGoToNext={handleGoToNext}
                onGoToPrevious={handleGoToPrevious}
                total={total}
              />
            </GalleryNavigationWrapper>

            <ScrollbarWrapper>
              <Scrollbar totalItems={total}>
                <div className={'gallery-scrollbar'} />
              </Scrollbar>
            </ScrollbarWrapper>
          </StyledContainer>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props & LayoutProps> = async ({
  locale
}) => {
  const initialData = await fetchHistory({ locale });

  if (isEmpty(initialData)) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      hasFooter: false,
      initialData: initialData ?? []
    },
    revalidate: 60 * 60 * 24
  };
};

/**
 * Export `OurStory` page.
 */

export default OurStory;
